import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { isMacLike, useKey } from '@poseidon/components/hooks/useKey';

import type { PaletteItem, PalettePage, RecentTarget } from '~/components/SuperadminSwitch/switchPalette.model';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { useAuthContext } from '~/auth/auth.context';
import { ADMIN_SECTIONS } from '~/components/superadmin/superadminPages';
import { SuperadminSwitchPalettePure } from '~/components/SuperadminSwitch/SuperadminSwitchPalettePure';
import { buildPaletteSections, flattenSections } from '~/components/SuperadminSwitch/switchPalette.model';
import { readRecents, useSwitchToUser } from '~/components/SuperadminSwitch/useSwitchToUser';
import { api } from '~/utils/api';

/** "⌘⇧K" on a Mac, "Ctrl+Shift+K" elsewhere — the one shortcut, shown wherever the palette is offered. */
export const switchShortcutLabel = () => (isMacLike() ? '⌘⇧K' : 'Ctrl+Shift+K');

const ALL_PAGES: PalettePage[] = ADMIN_SECTIONS.flatMap((s) =>
  s.cards.map((c) => ({ href: c.href, title: c.title, description: c.description })),
);
const DAILY_PAGES: PalettePage[] = (ADMIN_SECTIONS.find((s) => s.title === 'Daily')?.cards ?? []).map((c) => ({
  href: c.href,
  title: c.title,
  description: c.description,
}));

/**
 * The superadmin switch palette. Mount only for superadmins — it registers ⌘⇧K (Ctrl+Shift+K) globally,
 * including while typing, and owns search, switching, recents and page jumps.
 */
export function SuperadminSwitchPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const router = useRouter();
  const [auth] = useAuthContext();
  const { switchTo: switchToUser, switchBack } = useSwitchToUser();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [recents, setRecents] = useState<RecentTarget[]>([]);
  const setSearchDebounced = useDebouncedCallback(setSearch, 200);

  const toggle = useCallback(
    (e: KeyboardEvent) => {
      e.preventDefault();
      onOpenChange(!open);
    },
    [open, onOpenChange],
  );
  useKey('k', toggle, { metaWindowsCtrlKey: true, exactShift: true, allowInInputs: true });

  // Fresh palette every time it opens.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSearch('');
    setActiveId(null);
    setRecents(readRecents());
  }, [open]);

  const targetsQuery = api.superAdmin.switchTargets.useQuery(
    { q: search, includeInactive: showInactive || search.trim() !== '' },
    { enabled: open, placeholderData: (prev) => prev, staleTime: 60_000 },
  );

  const impersonation = auth.impersonation;
  const sections = useMemo(
    () =>
      buildPaletteSections({
        query,
        targets: targetsQuery.data ?? [],
        recents,
        pages: ALL_PAGES,
        dailyPages: DAILY_PAGES,
        impersonating: impersonation != null,
        currentUserId: impersonation?.userId ?? null,
      }),
    [query, targetsQuery.data, recents, impersonation],
  );

  // Keep a row highlighted: the first one whenever the list changes under the cursor.
  useEffect(() => {
    const items = flattenSections(sections);
    if (items.length === 0) setActiveId(null);
    else if (!items.some((it) => it.id === activeId)) setActiveId(items[0]!.id);
  }, [sections, activeId]);

  const close = () => onOpenChange(false);

  const switchTo = async (itemId: string, target: RecentTarget) => {
    setSwitchingId(itemId);
    try {
      // On failure the toast already said why; the palette stays open to pick again.
      if (await switchToUser(target)) close();
    } finally {
      setSwitchingId(null);
    }
  };

  const onSelect = (item: PaletteItem) => {
    if (item.kind === 'back') {
      switchBack();
      close();
    } else if (item.kind === 'page') {
      close();
      void router.push(item.page.href);
    } else if (item.kind === 'team') {
      void switchTo(item.id, item.switchTo);
    } else if (item.target.userId === impersonation?.userId) {
      close();
    } else {
      void switchTo(item.id, item.target);
    }
  };

  if (!open) return null;

  return (
    <SuperadminSwitchPalettePure
      query={query}
      onQueryChange={(q) => {
        setQuery(q);
        setSearchDebounced(q);
      }}
      sections={sections}
      activeId={activeId}
      onActiveIdChange={setActiveId}
      onSelect={onSelect}
      onClose={close}
      isLoading={targetsQuery.isFetching}
      switchingId={switchingId}
      showInactive={showInactive}
      onToggleInactive={() => setShowInactive((v) => !v)}
      shortcutLabel={switchShortcutLabel()}
    />
  );
}
